// services/api — the axios client for the THREE things that still use it.
//
// ── Why this file is small now ──────────────────────────────────────────────
//
// It used to export thirteen API objects — jobs, time entries, admin, workers,
// feedback, notifications, dashboard, estimates — wrapping endpoints that
// `apps/shared/http.js` already reaches. Measured 2026-09-07: 43 files import
// http.js; exactly THREE import this one, and between them they take only
// `authAPI`, `settingsAPI` and `wsService`. The other ten exports were
// imported by nothing at all.
//
// That is the shape the owner described as "duplicate data just because": two
// transport layers for one server, so the same endpoint had two callers with
// two error conventions, and a screen could be written against whichever one
// its author happened to find. `http.js` is the one that turns a 403 into a
// sentence a person can act on, which is why it is the one that survives.
//
// What is left, and why each stays rather than moving:
//
//   authAPI      login, register, getMe, profile, password. The signed-out
//                screens (components/Auth/*) and App.js's session bootstrap.
//                These run BEFORE the app shell exists and before http.js has
//                a token to send, so they are genuinely a different layer.
//   settingsAPI  App.js reads settings during that same bootstrap, choosing
//                the public row before login and the full row after.
//   wsService    a WebSocket, not HTTP at all. It only lives here because this
//                is where it was written.
//
// Do NOT add to this file. A new endpoint goes through apps/shared/http.js —
// see docs/ARCHITECTURE.md §6.

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors.
// v2.7.8 (SECURITY_TRACK #4): refined behavior so:
//   - 401 from /auth/login does NOT trigger redirect (it's just "wrong password";
//     letting the login page render its own error is correct).
//   - Any request can opt out by setting `config._skipAuthRedirect: true` —
//     useful for background polling or public-invoice flows that shouldn't
//     yank the user out of their current page.
//   - We stash a session-expired flash in sessionStorage so the login page
//     can show "Your session expired" instead of an unexplained redirect.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const cfg = error.config || {};
      const url = (cfg.url || '').toString();

      // 401s from the login endpoint itself are auth failures, not session
      // expirations — let the login page handle them inline.
      const isLoginAttempt = url.includes('/auth/login') || url.includes('/auth/register');

      // Per-request opt-out for callers that don't want to disrupt the user.
      const skip = cfg._skipAuthRedirect === true;

      if (!isLoginAttempt && !skip) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Only redirect if not already on the login/register flow.
        const onAuthPage = window.location.pathname.startsWith('/login')
          || window.location.pathname.startsWith('/register');

        if (!onAuthPage) {
          // Stash a flash so the login page can explain why the user is here.
          // Single-use: login page reads it once, clears it.
          try {
            sessionStorage.setItem('flash_auth', 'Your session expired. Please sign in again.');
          } catch (_) { /* sessionStorage may be unavailable in some contexts */ }
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
};







// Settings endpoints
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  // Branding only, readable without a token — everything else needs auth.
  getPublic: () => api.get('/settings/public'),
  getOne: (key) => api.get(`/settings/${key}`),
  update: (data) => api.put('/settings', { settings: data }),
  updateOne: (key, value) => api.put(`/settings/${key}`, { value }),
  uploadLogo: (logo) => api.post('/settings/upload-logo', { logo }),
};



// WebSocket connection
export class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect(token) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // v2.9.50: connect on a dedicated /ws path. nginx only forwards the
    // WebSocket upgrade on specific locations (/api, /socket.io, and now /ws);
    // the bare root hits the static-file location and never upgrades, which is
    // why live timers/notifications silently failed to connect.
    const wsUrl = process.env.REACT_APP_WS_URL || `${protocol}//${window.location.host}/ws`;
    
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      // Authenticate
      this.send({ type: 'authenticate', token });
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners(data.type, data);
      } catch (error) {
        console.error('WebSocket message error:', error);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      this.attemptReconnect(token);
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  attemptReconnect(token) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      setTimeout(() => this.connect(token), 3000 * this.reconnectAttempts);
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }
}

export const wsService = new WebSocketService();



export default api;
